import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { format, isToday } from 'date-fns';
import { CalendarClock, Clock } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
  DragOverlay,
} from '@dnd-kit/core';
import type { CollisionDetection } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../../store';
import { useFutureAwareTrackTasks, useAllTasks, updateTask } from '../../hooks/useTasks';
import { useAllProgressUpdates } from '../../hooks/useProgressNotes';
import { computeDayActivities } from '../../lib/summary';
import { useTrackDrag, type DragResult } from '../../hooks/useTrackDrag';
import { deleteTimelineBlock, createTimelineBlock } from '../../hooks/useTimelineBlocks';
import { snapToGrid, minutesToTime, PX_PER_HOUR, TIMELINE_START_HOUR, TIMELINE_END_HOUR, DEFAULT_DURATION } from '../../lib/timeline';
import { queryClient } from '../../api/queryClient';
import { api } from '../../api/client';
import { useToast } from '../../components/Toast/ToastProvider';
import type { TimelineBlock } from '../../types';
import { RaceTrack, createHorizontalClampedModifier } from './RaceTrack';
import { TrackDropZone } from './TrackDropZone';
import { QuickAdd } from '../shared/QuickAdd';
import { DailyPlanningPanel } from './DailyPlanningPanel';
import { TimelinePanel } from './Timeline/TimelinePanel';
import { TimelineDrawer } from './Timeline/TimelineDrawer';
import { DayGroup } from '../Summary/DayGroup';

export function TrackView() {
  const selectedDate = useAppStore((s) => s.selectedDate);
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const setHeaderContext = useAppStore((s) => s.setHeaderContext);
  const showTimelineDrawer = useAppStore((s) => s.showTimelineDrawer);
  const toggleTimelineDrawer = useAppStore((s) => s.toggleTimelineDrawer);
  const setShowTimelineDrawer = useAppStore((s) => s.setShowTimelineDrawer);
  const tasks = useFutureAwareTrackTasks(selectedDate);
  const [showPlanning, setShowPlanning] = useState(false);
  // Below this width: timeline panel hides inline and becomes an overlay drawer.
  // Math: parent col (~140) + racetrack lanes (~400 min) + gap (16) + timeline (480) = ~1036.
  // Adding breathing room → 1200px is the cleanest "side-by-side is comfortable" floor.
  const isNarrow = useMediaQuery('(max-width: 1199px)');
  const parentCount = tasks.filter((t) => !t.parentId).length;

  const allTasks = useAllTasks();
  const allProgressUpdates = useAllProgressUpdates();

  const qc = useQueryClient();
  const { addToast } = useToast();

  const timelineBlockDragMutation = useMutation({
    mutationFn: ({ blockId, scheduledStartTime }: { blockId: string; date: string; scheduledStartTime: string }) =>
      api.timelineBlocks.update(blockId, { scheduledStartTime }),
    onMutate: async ({ blockId, date, scheduledStartTime }) => {
      await qc.cancelQueries({ queryKey: ['timeline-blocks', date] });
      const previous = qc.getQueryData<TimelineBlock[]>(['timeline-blocks', date]);
      qc.setQueryData<TimelineBlock[]>(['timeline-blocks', date], (old) =>
        old?.map((b) => b.id === blockId ? { ...b, scheduledStartTime } : b)
      );
      return { previous, date };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(['timeline-blocks', context.date], context.previous);
      }
      addToast("Couldn't move block — position restored", 'error');
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ['timeline-blocks', vars.date] });
      qc.invalidateQueries({ queryKey: ['timeline-blocks', 'upcoming'] });
    },
  });

  // DndContext state lifted from RaceTrack
  const trackRef = useRef<HTMLDivElement>(null);
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const [notePrompt, setNotePrompt] = useState<DragResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [nearDone, setNearDone] = useState(false);
  const [isHorizontalDrag, setIsHorizontalDrag] = useState(false);
  const dragStartRowRect = useRef<DOMRect | null>(null);
  const nearDoneRef = useRef(false);
  const { handleDragEnd: handleSubtaskDragEnd } = useTrackDrag(trackRef, setNotePrompt, dragStartRowRect, nearDoneRef);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );
  const modifiers = useMemo(() => [createHorizontalClampedModifier(trackRef)], []);
  const parentTasks = useMemo(
    () => tasks.filter((t) => !t.parentId).sort((a, b) => (a.trackOrder ?? 0) - (b.trackOrder ?? 0)),
    [tasks]
  );

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      const isParentDrag = parentTasks.some((t) => (t.templateId ?? t.id) === args.active.id);
      if (isParentDrag) {
        // When dragging horizontally, only report drop zones (not sortable siblings)
        if (isHorizontalDrag) {
          const dropZones = pointerCollisions.filter(
            (c) => c.id === 'timeline-drop-zone' || c.id === 'track-drop-zone'
          );
          return dropZones.length > 0 ? dropZones : [];
        }
        // When dragging vertically, prioritize sortable siblings
        const sortableCollisions = pointerCollisions.filter(
          (c) => c.id !== 'timeline-drop-zone' && c.id !== 'track-drop-zone'
        );
        if (sortableCollisions.length > 0) return sortableCollisions;
      }
      return pointerCollisions;
    }
    return rectIntersection(args);
  }, [parentTasks, isHorizontalDrag]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    const activeData = active.data.current as Record<string, any> | undefined;

    // Compute time from pixel drop position using dynamic px-per-hour
    const dynamicStartHour = timelineScrollRef.current
      ? Number(timelineScrollRef.current.dataset.startHour ?? TIMELINE_START_HOUR)
      : TIMELINE_START_HOUR;
    const dynamicEndHour = timelineScrollRef.current
      ? Number(timelineScrollRef.current.dataset.endHour ?? TIMELINE_END_HOUR)
      : TIMELINE_END_HOUR;
    const dynamicPxPerHour = timelineScrollRef.current
      ? Number(timelineScrollRef.current.dataset.pxPerHour ?? PX_PER_HOUR)
      : PX_PER_HOUR;
    const pixelToMinutes = (px: number) => {
      return (px * 60) / dynamicPxPerHour + dynamicStartHour * 60;
    };

    // --- Timeline block being dragged ---
    if (activeData?.type === 'timeline-block') {
      if (activeData.spaceId && activeData.spaceId !== spaceId) return;
      const blockId = activeData.blockId as string;
      const taskId = activeData.taskId as string;

      // Dropped on track = delete the timeline block
      if (over?.id === 'track-drop-zone') {
        await deleteTimelineBlock(blockId, selectedDate);
        return;
      }

      // Dropped on timeline = reschedule to new time based on block's top edge
      if (over?.id === 'timeline-drop-zone') {
        const oldStartMin = activeData.scheduledStartTime
          ? (parseInt(activeData.scheduledStartTime.split(':')[0]) * 60 + parseInt(activeData.scheduledStartTime.split(':')[1]))
          : 0;
        const blockDuration = activeData.scheduledDuration ?? DEFAULT_DURATION;
        const oldTopPx = ((oldStartMin - dynamicStartHour * 60) * dynamicPxPerHour) / 60;
        const newTopPx = oldTopPx + event.delta.y;
        const minutes = pixelToMinutes(newTopPx);
        const clamped = Math.max(dynamicStartHour * 60, Math.min(dynamicEndHour * 60 - blockDuration, minutes));
        const snapped = snapToGrid(clamped);
        const timeStr = minutesToTime(snapped);
        timelineBlockDragMutation.mutate({ blockId, date: selectedDate, scheduledStartTime: timeStr });
        return;
      }

      // Dropped nowhere meaningful — do nothing
      void taskId; // taskId kept in payload for future use
      return;
    }

    // --- Task/subtask being dragged to timeline ---
    if (over?.id === 'timeline-drop-zone') {
      const data = active.data.current as { taskId?: string; trackProgress?: number; title?: string; priority?: string; blocked?: boolean; type?: string; itemType?: TimelineBlock['itemType']; parentId?: string | null } | undefined;
      const taskId = data?.taskId ?? (active.id as string);
      if (data?.blocked) return;

      const timelineEl = timelineScrollRef.current;
      if (!timelineEl) return;

      const timelineRect = timelineEl.getBoundingClientRect();
      const activatorEvent = event.activatorEvent as MouseEvent;
      if (!activatorEvent) return;

      const dropY = activatorEvent.clientY + event.delta.y - timelineRect.top + timelineEl.scrollTop;
      const minutes = pixelToMinutes(dropY);
      const clamped = Math.max(dynamicStartHour * 60, Math.min(dynamicEndHour * 60 - DEFAULT_DURATION, minutes));
      const snapped = snapToGrid(clamped);
      const timeStr = minutesToTime(snapped);

      await createTimelineBlock(taskId, selectedDate, timeStr, undefined, {
        title: data?.title,
        priority: data?.priority,
        trackProgress: data?.trackProgress,
        itemType: data?.itemType,
        parentId: data?.parentId,
      });
      return;
    }

    // Handle normal track drags
    const isParentDrag = parentTasks.some((t) => t.id === active.id);

    if (!over || active.id === over.id) {
      if (!isParentDrag) {
        await handleSubtaskDragEnd(event);
      }
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;
    const isParentReorder = parentTasks.some((t) => t.id === activeId) && parentTasks.some((t) => t.id === overId);

    if (isParentReorder) {
      const oldIdx = parentTasks.findIndex((t) => t.id === activeId);
      const newIdx = parentTasks.findIndex((t) => t.id === overId);
      if (oldIdx !== -1 && newIdx !== -1) {
        const reordered = [...parentTasks];
        const [moved] = reordered.splice(oldIdx, 1);
        reordered.splice(newIdx, 0, moved);
        const trackQueryKey = ['tasks', spaceId, 'track'];
        await queryClient.cancelQueries({ queryKey: trackQueryKey });
        const previous = queryClient.getQueryData(trackQueryKey);
        queryClient.setQueryData(trackQueryKey, (old: any[] | undefined) => {
          if (!old) return old;
          const orderMap = new Map(reordered.map((t, i) => [t.id, i]));
          return old.map((t: any) => orderMap.has(t.id) ? { ...t, trackOrder: orderMap.get(t.id)! } : t);
        });
        try {
          await Promise.all(reordered.map((t, i) => updateTask(t.id, { trackOrder: i })));
        } catch {
          if (previous) queryClient.setQueryData(trackQueryKey, previous);
          addToast("Couldn't save order — reverted", 'error');
        } finally {
          queryClient.invalidateQueries({ queryKey: trackQueryKey });
        }
      }
      return;
    }

    const taskId = active.id as string;
    const draggedTask = tasks.find((t) => t.id === taskId);
    const parentId = draggedTask?.parentId;
    await handleSubtaskDragEnd(event);
    // Do NOT invalidate ['tasks', spaceId, 'track'] here - useTrackDrag's onSettled handles it.
    // Doing it here races against the optimistic update and causes snap-back on drag-to-done.
    if (parentId) {
      queryClient.invalidateQueries({ queryKey: ['subtasks', parentId] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['subtasks'] });
    }
    queryClient.invalidateQueries({ queryKey: ['progress-updates', taskId] });
  }, [parentTasks, tasks, spaceId, selectedDate, handleSubtaskDragEnd, timelineBlockDragMutation]);

  const isPast = !isToday(new Date(selectedDate + 'T00:00:00')) && selectedDate < format(new Date(), 'yyyy-MM-dd');

  const activities = useMemo(
    () => computeDayActivities(allTasks, allProgressUpdates, selectedDate),
    [allTasks, allProgressUpdates, selectedDate]
  );

  useEffect(() => {
    setHeaderContext({ count: parentCount, label: 'on track' });
    return () => setHeaderContext(null);
  }, [parentCount, setHeaderContext]);

  // Auto-close the drawer when the viewport grows past the breakpoint —
  // the inline panel takes over there, so leaving the drawer open would
  // re-open it on a subsequent resize back to narrow with no user action.
  useEffect(() => {
    if (!isNarrow && showTimelineDrawer) {
      setShowTimelineDrawer(false);
    }
  }, [isNarrow, showTimelineDrawer, setShowTimelineDrawer]);

  if (isPast) {
    return (
      <div className="p-6 space-y-4 max-w-3xl mx-auto">
        {activities.length === 0 ? (
          <>
            <h2 className="text-headline text-text-primary">
              {format(new Date(selectedDate + 'T00:00:00'), 'EEEE, MMM d')}
            </h2>
            <p className="text-body text-text-muted">No activity recorded for this day.</p>
          </>
        ) : (
          <DayGroup date={new Date(selectedDate + 'T00:00:00')} activities={activities} />
        )}
      </div>
    );
  }

  return (
    <div className="h-full relative">
      <div className="h-full overflow-auto">
        <div className="p-6 space-y-4 min-h-full min-w-[700px]">
        <div className="flex items-center justify-end gap-3">
        {isNarrow && (
          <button
            onClick={toggleTimelineDrawer}
            aria-pressed={showTimelineDrawer}
            aria-label={showTimelineDrawer ? 'Hide timeline' : 'Show timeline'}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-caption font-medium transition-colors ${
              showTimelineDrawer ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-text-secondary interactive-ghost'
            }`}
          >
            <Clock size={14} />
            Timeline
          </button>
        )}
        <button
          onClick={() => setShowPlanning(!showPlanning)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-caption font-medium transition-colors ${
            showPlanning ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-text-secondary interactive-ghost'
          }`}
        >
          <CalendarClock size={14} />
          Plan Day
        </button>
        <QuickAdd
          defaultStatus="track"
          trackDate={selectedDate}
          placeholder="Add to track..."
        />
        </div>
        <AnimatePresence>
          {showPlanning && <DailyPlanningPanel onClose={() => setShowPlanning(false)} />}
        </AnimatePresence>
        <DndContext sensors={sensors} modifiers={modifiers} collisionDetection={collisionDetection} onDragStart={(e) => {
          setIsDragging(true);
          setActiveDragId(String(e.active.id));
          setIsHorizontalDrag(false);
          const me = e.activatorEvent as MouseEvent | undefined;
          if (me) {
            const els = document.elementsFromPoint(me.clientX, me.clientY);
            const row = els.find(el => (el as HTMLElement).hasAttribute?.('data-track-row')) as HTMLElement | undefined;
            dragStartRowRect.current = row?.getBoundingClientRect() ?? null;
          }
        }} onDragMove={(e) => {
          const isParent = parentTasks.some((t) => (t.templateId ?? t.id) === e.active.id);
          if (isParent && Math.abs(e.delta.x) > 60 && Math.abs(e.delta.x) > Math.abs(e.delta.y) * 2) {
            setIsHorizontalDrag(true);
          } else if (isParent && Math.abs(e.delta.y) > Math.abs(e.delta.x)) {
            setIsHorizontalDrag(false);
          }
        }} onDragEnd={(e) => { setIsDragging(false); setActiveDragId(null); setNearDone(false); setIsHorizontalDrag(false); handleDragEnd(e); dragStartRowRect.current = null; }} onDragCancel={() => { setIsDragging(false); setActiveDragId(null); setNearDone(false); setIsHorizontalDrag(false); dragStartRowRect.current = null; }}>
          <div className="flex items-start gap-4">
            <TrackDropZone>
              <RaceTrack
                tasks={tasks}
                trackRef={trackRef}
                notePrompt={notePrompt}
                setNotePrompt={setNotePrompt}
                isDragging={isDragging}
                activeDragId={activeDragId}
                nearDone={nearDone}
                onNearDoneChange={setNearDone}
                nearDoneRef={nearDoneRef}
              />
            </TrackDropZone>
            {!isNarrow && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
                className="sticky top-4 h-[calc(100vh-6rem)] shrink-0 w-[480px]"
              >
                <TimelinePanel scrollRef={timelineScrollRef} />
              </motion.div>
            )}
          </div>
          <DragOverlay dropAnimation={null}>
            {isHorizontalDrag && activeDragId && (() => {
              const t = tasks.find((t) => (t.templateId ?? t.id) === activeDragId || t.id === activeDragId);
              if (!t) return null;
              return (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-bg-raised border border-accent/50 shadow-lg shadow-accent/20 max-w-56">
                  <Clock size={12} className="text-accent shrink-0" />
                  <span className="text-caption font-medium text-text-primary truncate">{t.title}</span>
                </div>
              );
            })()}
          </DragOverlay>
        </DndContext>
        </div>
      </div>
      {isNarrow && (
        <TimelineDrawer
          open={showTimelineDrawer}
          onClose={() => setShowTimelineDrawer(false)}
          scrollRef={timelineScrollRef}
        />
      )}
    </div>
  );
}
